import { useCallback, useEffect, useState } from "react";

import { ApiFailure, apiFetch } from "./lib/api.js";

export interface Resource<T> {
  error?: ApiFailure;
  loading: boolean;
  reload: () => void;
  value?: T;
}

/** Fetch a path, re-fetching whenever it changes or `reload` is called. */
export function useResource<T>(path: string | null): Resource<T> {
  const [value, setValue] = useState<T>();
  const [error, setError] = useState<ApiFailure>();
  const [loading, setLoading] = useState(Boolean(path));
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((count) => count + 1), []);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    apiFetch<T>(path)
      .then((result) => {
        if (!live) return;
        setValue(result);
        setError(undefined);
      })
      .catch((caught) => {
        if (!live) return;
        setError(
          caught instanceof ApiFailure
            ? caught
            : new ApiFailure(0, [{ status: 0, title: String(caught) }]),
        );
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [path, nonce]);

  return { error, loading, reload, value };
}
